import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import { useKeycloak } from "@react-keycloak/web";
import { NavLink, useLocation } from "react-router-dom";
import { NavBarSortedUp, NavBarSortedDown } from "./ProjectIcons";
import type { KeycloakTokenParsed } from 'keycloak-js';
import { EulaModal } from "./EulaModal";
import { getEula, getMyDetails } from "../../api/users/usersApi";
import SelectInput from "./SelectInput";
// Import multilanguage utilities as needed
import { useTimezone } from "../../context/TimezoneContext";
import { getUtcOffsets } from "./getUtcOffsets";
import { KEYCLOAK_INTENTIONAL_LOGOUT_KEY } from "./useKeycloak";
import { useScrollLock } from "./useScrollLock";
import { toast } from "react-toastify";

type ParsedToken = KeycloakTokenParsed & {
  email?: string
  preferred_username?: string
  given_name?: string
  family_name?: string
}
// Track navbar visibility based on scroll

const getEnvironment = () => {
  const hostname = window.location.hostname;

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return { name: 'local', color: 'local-env-stripe' };
  } else if (hostname.includes('dev.gundiservice.org')) {
    return { name: 'dev', color: 'dev-env-stripe' };
  } else if (hostname.includes('stage.gundiservice.org')) {
    return { name: 'stage', color: 'stage-env-stripe' };
  } else {
    return { name: '', color: '' };
  }
};

const Nav = () => {
  const { t } = useTranslation();
  const { keycloak } = useKeycloak();
  const divRef = useRef<HTMLDivElement | null>(null);
  // Check both localStorage and keycloak.authenticated to handle session expiration
  const isLoggedIn = keycloak?.authenticated && localStorage.getItem("userAuthorized") !== null
  const [timezones, setTimezones] = useState<any[]>([]);
  const { timezone, setTimezone } = useTimezone();
  const [subMenu, setSubMenu] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const closeMobileMenu = () => setMobileMenuOpen(false);
  const [showEULAModal, setShowEULAModal] = useState(false);
  const [visible, setVisible] = useState(true);
  const lastScrollY = useRef(window.pageYOffset);
  const [environment, setEnvironment] = useState({ name: '', color: '' });
  const parsedToken: ParsedToken | undefined = keycloak?.tokenParsed;
  const [displayName, setDisplayName] = useState("");
  // null = unknown (not yet fetched or fetch failed), true/false = known
  const [profileComplete, setProfileComplete] = useState<boolean | null>(null);
  const location = useLocation();

  const handleNavClick = (e: React.MouseEvent) => {
    if (location.pathname === "/account" && profileComplete === false) {
      e.preventDefault();
      toast.warning(t("Please fill in your contact information before continuing."), { position: toast.POSITION.TOP_RIGHT });
    }
  };
  useScrollLock(mobileMenuOpen);
  const toggleSubMenu = () => setSubMenu(prev => !prev);

  const getEulaStatus = React.useCallback(async () => {
    if (keycloak.token) {
      const results = await getEula(keycloak.token);
      if (!results.accepted) {
        setShowEULAModal(true);
      }
    }
  }, [keycloak.token])

  const loadDisplayName = React.useCallback(async () => {
    if (keycloak.token) {
      setProfileComplete(null);
      const pt: ParsedToken | undefined = keycloak.tokenParsed as ParsedToken | undefined;
      const tokenName = [pt?.given_name?.trim(), pt?.family_name?.trim()].filter(Boolean).join(" ");
      try {
        const profile = await getMyDetails(keycloak.token);
        const apiName = [profile?.first_name?.trim(), profile?.last_name?.trim()].filter(Boolean).join(" ");
        setDisplayName(apiName || tokenName || pt?.preferred_username?.trim() || pt?.email?.trim() || "");
        setProfileComplete(profile
          ? !!(profile.first_name?.trim() && profile.last_name?.trim() && profile.contact_email?.trim())
          : null);
      } catch {
        setDisplayName(tokenName || pt?.preferred_username?.trim() || pt?.email?.trim() || "");
        setProfileComplete(null);
      }
    }
  }, [keycloak.token, keycloak.tokenParsed])

  const logoutUser = () => {
    // Set flag to indicate intentional logout (prevents showing "Session Expired" modal)
    // Use localStorage so it persists even if sessionStorage is cleared
    localStorage.setItem(KEYCLOAK_INTENTIONAL_LOGOUT_KEY, 'true');
    keycloak.logout({ redirectUri: window.location.origin });
    localStorage.removeItem("userAuthorized");
    sessionStorage.clear();
    setTimezone({ label: "UTC", value: "+0000" });
  }

  useEffect(() => {
    keycloak.onAuthSuccess = () => {
      if (!keycloak.isTokenExpired(0)) {
        localStorage.setItem("userAuthorized", "true");
        // Clear the intentional logout flag on successful login
        localStorage.removeItem(KEYCLOAK_INTENTIONAL_LOGOUT_KEY);
        keycloak.loadUserProfile();
        getEulaStatus();
        loadDisplayName();
      }
    };

    function handleClickOutside(event: any) {
      if (divRef.current && !divRef.current.contains(event.target) && !event.target.closest("a")) {
        setSubMenu(false);
      }
    }

    if (isLoggedIn && !displayName) {
      loadDisplayName();
    }

    let temp = getUtcOffsets();
    setTimezones(temp);

    setEnvironment(getEnvironment());

    const updateNavbarVisibility = () => {
      const currentScrollY = window.pageYOffset;

      // Navbar hide/show on scroll is temporarily disabled.
      // When re-enabling, consider using a scroll threshold to prevent flickering on small movements:
      // const SCROLL_THRESHOLD = 10;
      // const scrollDelta = currentScrollY - lastScrollY.current;
      // if (scrollDelta < -SCROLL_THRESHOLD) {
      //   setVisible(true);
      // } else if (scrollDelta > SCROLL_THRESHOLD) {
      //   setVisible(false);
      // }

      lastScrollY.current = currentScrollY;
    };

    const handleProfileUpdated = (e: Event) => {
      const { first_name, last_name, contact_email } = (e as CustomEvent).detail;
      const apiName = [first_name?.trim(), last_name?.trim()].filter(Boolean).join(" ");
      const tokenName = [parsedToken?.given_name?.trim(), parsedToken?.family_name?.trim()].filter(Boolean).join(" ");
      setDisplayName(apiName || tokenName || parsedToken?.preferred_username?.trim() || parsedToken?.email?.trim() || "");
      setProfileComplete(!!(first_name?.trim() && last_name?.trim() && contact_email?.trim()));
    };

    window.addEventListener("scroll", updateNavbarVisibility);
    window.addEventListener("userProfileUpdated", handleProfileUpdated);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener("scroll", updateNavbarVisibility);
      window.removeEventListener("userProfileUpdated", handleProfileUpdated);
    };
  }, [keycloak, getEulaStatus, loadDisplayName]);

  const handleSelectTimezone = (e: any) => {
    setTimezone(e);
    localStorage.setItem("selectedTimezone", JSON.stringify(e));
  }

  const nameInitials = displayName.trim() ? displayName.split(' ').filter(Boolean).map(n => n[0].toUpperCase()).join('') : '';
  const shortTimezone = timezone?.label.match(/UTC[+-]\d+(?::\d+)?/)?.[0] ?? timezone?.label;

  return (
    <div>
      <div data-testid="main-nav-wrapper" className={`fixed top-0 z-50 w-full transition-transform duration-300 ${visible ? 'translate-y-0' : '-translate-y-full'}`}>
        {environment.name !== 'production' && environment.name !== '' && (
          <div className={`${environment.color} text-xs font-bold text-center py-1.5`}></div>
        )}
        <section className="x-auto">
          <nav className="bg-white text-secondary-gray w-screen shadow-sm shadow-gray-lines px-8">
            <div className="flex justify-between py-6 w-full max-w-list-table mx-auto items-center">
              <div className="flex flex-row items-end">
                <img src={require('../../images/gundi_logo.png')} alt="gundi logo" className="h-8 mr-2" />
                <h3 className="text-2xl font-bold text-secondary-gray font-heading -mb-1 mr-6 pb-0">
                  {process.env.REACT_APP_TEST_VAR}
                </h3>

                {isLoggedIn && (
                  <ul className="hidden md:flex px-4 mx-auto font-semibold font-heading space-x-4 lg:space-x-8 whitespace-nowrap">
                    <li>
                      <NavLink data-testid="nav_connections_btn" to="/" onClick={handleNavClick}>
                        {({ isActive }) => (<span className={isActive ? "pb-6 text-accent-green border-2 border-white border-b-accent-green" : "hover:text-neutral-gray-400 border-2 border-white"}>{t("Connections")}</span>)}
                      </NavLink>
                    </li>
                    <li>
                      <NavLink data-testid="nav_destinations_btn" to="/destinations" onClick={handleNavClick}>
                        {({ isActive }) => (<span className={isActive ? "pb-6 text-accent-green border-2 border-white border-b-accent-green" : "hover:text-neutral-gray-400 border-2 border-white"}>{t("Destinations")}</span>)}
                      </NavLink>
                    </li>
                    <li>
                      <NavLink data-testid="nav_sources_btn" to="/sources" onClick={handleNavClick}>
                        {({ isActive }) => (<span className={isActive ? "pb-6 text-accent-green border-2 border-white border-b-accent-green" : "hover:text-neutral-gray-400 border-2 border-white"}>{t("Sources")}</span>)}
                      </NavLink>
                    </li>
                    <li>
                      <NavLink data-testid="nav_organizations_btn" to="/workspaces" onClick={handleNavClick}>
                        {({ isActive }) => (<span className={isActive ? "pb-6 text-accent-green border-2 border-white border-b-accent-green" : "hover:text-neutral-gray-400 border-2 border-white"}>{t("Workspaces")}</span>)}
                      </NavLink>
                    </li>
                  </ul>
                )}
              </div>

              <div className="flex flex-row text-neutral-gray-900 items-center flex-shrink-0">
                {isLoggedIn && (
                  <>
                    <div className="hidden md:flex flex-row items-center">
                      {/* tz select — compact label at md, full label at lg+ */}
                      <div className="mx-3 cursor-pointer flex justify-end w-[120px] min-[1125px]:w-[220px] xl:w-[317px]" title={timezone?.label}>
                        <SelectInput
                          options={timezones}
                          data-testid="select_timezone_activity_logs_filter_compact"
                          className={"basic-single text-sm min-[1125px]:hidden"}
                          value={{ label: shortTimezone, value: timezone?.value }}
                          isDisabled={false}
                          isLoading={false}
                          isClearable={false}
                          isSearchable={false}
                          isMulti={false}
                          name={"Filter by Workspace"}
                          onChange={(e: any) => handleSelectTimezone(e)}
                          variant="navTimezone"
                        />
                        <SelectInput
                          options={timezones}
                          data-testid="select_timezone_activity_logs_filter_full"
                          className={"basic-single text-sm hidden min-[1125px]:block"}
                          value={timezone}
                          isDisabled={false}
                          isLoading={false}
                          isClearable={false}
                          isSearchable={true}
                          isMulti={false}
                          name={"Filter by Workspace"}
                          onChange={(e: any) => handleSelectTimezone(e)}
                          variant="navTimezone"
                        />
                      </div>
                    </div>
                  </>
                )}
                {/* profile dropdown */}
                {isLoggedIn && (
                  <div ref={divRef} className="hidden md:flex relative">
                      <div onClick={toggleSubMenu} className="text-secondary-text cursor-pointer flex items-center gap-1">
                        <span className="text-base font-medium text-grey-g0">
                          <span className="min-[1125px]:hidden">{nameInitials}</span>
                          <span className="hidden min-[1125px]:inline truncate max-w-[200px]">{displayName}</span>
                        </span>
                        {subMenu ? <NavBarSortedUp /> : <NavBarSortedDown />}
                      </div>
                      <div className={!subMenu ? "hidden" : "z-30 absolute top-full mt-1 bg-white shadow-[0_0_5px_0_rgba(0,0,0,0.25)] rounded overflow-hidden right-0"}>
                        <ul role="menu" className="text-base font-normal text-secondary-text whitespace-nowrap">
                          <li role="none" className="cursor-pointer hover:bg-light-blue-hover">
                            <NavLink
                              role="menuitem"
                              to="/account"
                              data-testid="nav_account_btn"
                              onClick={() => setSubMenu(false)}
                              className="flex items-center gap-2 h-10 px-4"
                            >
                              {t("Account Settings")}
                            </NavLink>
                          </li>
                          <li role="none" className="hover:bg-light-blue-hover">
                            <button
                              type="button"
                              role="menuitem"
                              data-testid="nav_logout_btn"
                              className="flex items-center gap-2 h-10 px-4 w-full text-left cursor-pointer"
                              onClick={logoutUser}
                            >
                              {t("Log Out")}
                            </button>
                          </li>
                        </ul>
                      </div>
                  </div>
                )}
                {/* Hamburger button — visible only on small screens */}
                {isLoggedIn && (
                  <button
                    className="md:hidden ml-3 text-secondary-gray"
                    onClick={() => setMobileMenuOpen(true)}
                    aria-label="Open menu"
                    aria-expanded={mobileMenuOpen}
                    aria-controls="mobile-menu"
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M3 6h18M3 12h18M3 18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </nav>
        </section>
      </div>
      {/* Mobile full-screen menu */}
      {mobileMenuOpen && isLoggedIn && (
        <>
        {/* Backdrop — darkens content behind the drawer */}
        <div
          className="fixed inset-0 z-[59] md:hidden"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.4)' }}
          onClick={closeMobileMenu}
          aria-hidden="true"
        />
        <div id="mobile-menu" className="fixed top-0 left-0 right-0 z-[60] bg-white flex flex-col shadow-lg max-h-screen overflow-y-auto md:hidden">
          {/* Menu header */}
          <div className="flex items-center justify-between pl-5 pr-8 py-6">
            <img src={require('../../images/gundi_logo.png')} alt="gundi logo" className="h-8" />
            <div className="flex items-center gap-8">
              <span className="text-secondary-gray text-base font-semibold truncate max-w-[247px]">
                {displayName}
              </span>
              <button onClick={closeMobileMenu} className="text-secondary-gray" aria-label="Close menu">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M15 5L5 15M5 5l10 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
          </div>

          {/* Nav links */}
          <ul className="px-5 py-6 space-y-6 flex-1">
            <li>
              <NavLink to="/" onClick={(e) => { handleNavClick(e); if (location.pathname !== "/account") closeMobileMenu(); }}>
                {({ isActive }) => (
                  <span className={isActive ? "text-accent-green font-semibold text-lg" : "text-secondary-gray font-semibold text-lg"}>
                    {t("Connections")}
                  </span>
                )}
              </NavLink>
            </li>
            <li>
              <NavLink to="/destinations" onClick={(e) => { handleNavClick(e); if (location.pathname !== "/account") closeMobileMenu(); }}>
                {({ isActive }) => (
                  <span className={isActive ? "text-accent-green font-semibold text-lg" : "text-secondary-gray font-semibold text-lg"}>
                    {t("Destinations")}
                  </span>
                )}
              </NavLink>
            </li>
            <li>
              <NavLink to="/sources" onClick={(e) => { handleNavClick(e); if (location.pathname !== "/account") closeMobileMenu(); }}>
                {({ isActive }) => (
                  <span className={isActive ? "text-accent-green font-semibold text-lg" : "text-secondary-gray font-semibold text-lg"}>
                    {t("Sources")}
                  </span>
                )}
              </NavLink>
            </li>
            <li>
              <NavLink to="/workspaces" onClick={(e) => { handleNavClick(e); if (location.pathname !== "/account") closeMobileMenu(); }}>
                {({ isActive }) => (
                  <span className={isActive ? "text-accent-green font-semibold text-lg" : "text-secondary-gray font-semibold text-lg"}>
                    {t("Workspaces")}
                  </span>
                )}
              </NavLink>
            </li>
          </ul>

          {/* Timezone + language + logout at the bottom */}
          <div className="px-5 pb-6 space-y-8">
            <div className="-ml-2">
              <SelectInput
                options={timezones}
                className="basic-single text-sm"
                value={timezone}
                isDisabled={false}
                isLoading={false}
                isClearable={false}
                isSearchable={true}
                isMulti={false}
                name="Filter by Workspace"
                onChange={(e: any) => handleSelectTimezone(e)}
                variant="navTimezoneMobile"
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>
            <NavLink
              to="/account"
              data-testid="nav_mobile_account_btn"
              className="block w-full text-left text-secondary-gray font-semibold text-lg"
              onClick={closeMobileMenu}
            >
              {t("Account Settings")}
            </NavLink>
            <button
              data-testid="nav_mobile_logout_btn"
              className="w-full text-left text-secondary-gray font-semibold text-lg"
              onClick={() => { closeMobileMenu(); logoutUser(); }}
            >
              {t("Log Out")}
            </button>
          </div>
        </div>
        </>
      )}

      {showEULAModal &&
        <EulaModal />
      }
    </div>
  );
};

export default Nav;